export type ArtLabPanel = "body" | "rig" | "vfx" | "composite" | "contract";

export type ArtLabPackId =
  | "dropfront"
  | "portal-ffa"
  | "frontline"
  | "raid"
  | "infinite-war";

/**
 * One Character = Spine-style skeleton identity.
 * body clip + UnitRig weapon + attack kit all share `characterId`.
 */
export type ArtLabState = {
  panel: ArtLabPanel;
  packId: ArtLabPackId;
  characterId: string;
  affiliation: string;
  facing: "e" | "n" | "s" | "w";
  anim: "idle" | "walk" | "attack";
  animTime: number;
  aimDeg: number;
  bobOn: boolean;
  vfxAngleDeg: number;
  autoPlay: boolean;
  /** Composite completion bits — required before Contract PASS stamp. */
  seenBodyAttack: boolean;
  seenCompositeAlly: boolean;
  seenCompositeEnemy: boolean;
};

export const DF_COMBAT = [
  "assault",
  "shooter",
  "engineer",
  "swarm",
] as const;

/** Structures + board tiles for Art Lab Props panel. */
export const DF_PROPS = [
  "hq",
  "depot",
  "nest",
  "turret",
  "beacon",
  "barrier",
  "pod",
  "tile_floor",
  "tile_floor_alt",
  "tile_rift",
  "tile_rift_edge",
] as const;

export const FFA_COMBAT = [
  "master",
  "guardian",
  "soldier",
  "raider",
  "chaser",
  "hero",
  "weapon",
  "mana",
] as const;

/** Frontline T1 (dropfront sprites) + T2/T3 (frontline pack). */
export const FL_COMBAT = [
  "assault",
  "shooter",
  "swarm",
  "fl_t2_vanguard",
  "fl_t2_marksman",
  "fl_t2_reaver",
  "fl_t2_warden",
  "fl_t3_colossus",
  "fl_t3_siege",
  "fl_t3_shadow",
  "fl_t3_storm",
  "fl_t3_paladin",
] as const;

export const RAID_COMBAT = [
  "raid_tank",
  "raid_dps",
  "raid_healer",
  "raid_boss",
  "raid_add",
  "raid_brute",
  "raid_charger",
] as const;

export const IW_COMBAT = [
  "iw_guardian",
  "iw_paladin",
  "iw_seraph",
  "iw_choir",
  "iw_judge",
  "iw_shieldwall",
  "iw_radiant",
  "iw_archangel",
  "iw_wraith",
  "iw_skelpike",
  "iw_plague",
  "iw_reaper",
  "iw_priest",
  "iw_deathknight",
  "iw_necrocannon",
  "iw_lich",
] as const;

export const ART_LAB_PACKS: readonly ArtLabPackId[] = [
  "dropfront",
  "portal-ffa",
  "frontline",
  "raid",
  "infinite-war",
];

export function charactersForPack(packId: ArtLabPackId): readonly string[] {
  if (packId === "dropfront") return [...DF_COMBAT, ...DF_PROPS];
  if (packId === "portal-ffa") return FFA_COMBAT;
  if (packId === "raid") return RAID_COMBAT;
  if (packId === "infinite-war") return IW_COMBAT;
  return FL_COMBAT;
}

export function affiliationsForPack(packId: ArtLabPackId): readonly string[] {
  return packId === "portal-ffa"
    ? ["ally", "enemy1", "enemy2"]
    : ["ally", "enemy"];
}

/**
 * Sprite pack for ArtPipeline — Frontline T1 reuses dropfront bodies.
 */
export function spritePackForCharacter(
  packId: ArtLabPackId,
  characterId: string,
): string {
  if (packId === "frontline" && !characterId.startsWith("fl_")) {
    return "dropfront";
  }
  return packId;
}

export function defaultArtLabState(): ArtLabState {
  return {
    panel: "body",
    packId: "dropfront",
    characterId: "assault",
    affiliation: "ally",
    facing: "e",
    anim: "idle",
    animTime: 0,
    aimDeg: 0,
    bobOn: true,
    vfxAngleDeg: 0,
    autoPlay: true,
    seenBodyAttack: false,
    seenCompositeAlly: false,
    seenCompositeEnemy: false,
  };
}

/** Pack switch resets character + affiliation; keeps panel/pose knobs. */
export function applyPackChange(
  state: ArtLabState,
  packId: ArtLabPackId,
): ArtLabState {
  const chars = charactersForPack(packId);
  return {
    ...state,
    packId,
    characterId: chars[0]!,
    affiliation: affiliationsForPack(packId)[0]!,
    seenBodyAttack: false,
    seenCompositeAlly: false,
    seenCompositeEnemy: false,
  };
}

/** True when Composite walkthrough bits are complete for Contract stamp. */
export function compositeBitsComplete(state: ArtLabState): boolean {
  return (
    state.seenBodyAttack &&
    state.seenCompositeAlly &&
    state.seenCompositeEnemy
  );
}

export function markCompositeSeen(
  state: ArtLabState,
  affiliation: string,
): Partial<ArtLabState> {
  const patch: Partial<ArtLabState> = {};
  if (affiliation === "ally" || affiliation.startsWith("ally")) {
    patch.seenCompositeAlly = true;
  }
  if (affiliation.startsWith("enemy")) {
    patch.seenCompositeEnemy = true;
  }
  return patch;
}
